import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  Observable,
  of,
  defer,
  catchError,
  from,
  map,
  take,
  filter,
  tap,
} from 'rxjs';
import { CookieService } from '@shared/services/cookie/cookie.service';
import { ServiceHandlerService } from '@shared/services/service-handler.service';
import { Capacitor } from '@capacitor/core';
import { App, AppLaunchUrl } from '@capacitor/app';

export type MarketingEventData = OrderEventData; // | FutureInterface;

interface OrderEventData {
  caseCount: number;
  orderValue: number;
}

export interface MetaCookieData {
  fbc?: string;
  fbp?: string;
}

export interface ClientData {
  meta?: MetaCookieData;
}

export interface MarketingConversionPayload {
  marketingConversion: {
    event: string;
    userData: {
      firstName: string;
      lastName: string;
      email: string;
    };
    customerData: {
      city: string;
      state: string;
      zip: number;
    };
    clientData: ClientData;
    eventData: MarketingEventData;
  };
}

@Injectable({
  providedIn: 'root',
})
export class MarketingService {
  private static FBC_COOKIE_NAME = '_fbc';
  private static FBC_QUERY_PARAM_NAME = 'fbclid';
  private static FBC_COOKIE_EXPIRATION_DAYS = 90;
  private static SUBDOMAIN_INDEX = 1;
  private static COOKIE_PATH = '/';

  constructor(
    private readonly browserWindow: Window,
    private readonly serviceHandler: ServiceHandlerService,
    private readonly browserCookieService: CookieService,
  ) {}

  sendLinkConversionEvent(
    payload: MarketingConversionPayload,
  ): Observable<{ success: boolean }> {
    return this.serviceHandler.post<{ success: boolean }>(
      `${environment.alertsApiUrl}/marketingConversions`,
      payload,
    );
  }

  public checkForMetaClickId(): Observable<void> {
    return defer(() => {
      try {
        const isNative = Capacitor.isNativePlatform();
        if (isNative) {
          // observable from the launchUrl
          return this.createNativeMetaClickIdObservable(
            from(Promise.resolve(App.getLaunchUrl())),
          );
        } else {
          // observable from the browser window location
          return this.createBrowserMetaClickIdObservable(
            of(this.browserWindow?.location),
          );
        }
      } catch (err) {
        // Convert synchronous errors to observable errors
        return of(undefined);
      }
    }).pipe(
      catchError(error => {
        console.error('Failed to initialize meta click id conversion', error);
        // On error, we still complete the stream to prevent leaks.
        return of(undefined);
      }),
    );
  }

  private createNativeMetaClickIdObservable(
    url$: Observable<AppLaunchUrl>,
  ): Observable<void> {
    return url$.pipe(
      map(launchUrl => launchUrl?.url ?? ''),
      map(url => {
        const search = this.getSearchFromUrl(url);
        return this.extractQueryParameter(
          search,
          MarketingService.FBC_QUERY_PARAM_NAME,
        );
      }),
      take(1),
      filter(clickIdFromUrl => !!clickIdFromUrl),
      tap(clickIdFromUrl => {
        const existingClickId = this.parseClickId(
          localStorage.getItem(MarketingService.FBC_COOKIE_NAME),
        );
        if (clickIdFromUrl !== existingClickId) {
          const cookieResult = this.buildMetaCookieValue(clickIdFromUrl);
          localStorage.setItem(
            MarketingService.FBC_COOKIE_NAME,
            cookieResult.value,
          );
        }
      }),
      map(() => undefined),
    );
  }

  private createBrowserMetaClickIdObservable(
    location$: Observable<Location>,
  ): Observable<void> {
    return location$.pipe(
      map(location =>
        this.extractQueryParameter(
          location?.search ?? '',
          MarketingService.FBC_QUERY_PARAM_NAME,
        ),
      ),
      take(1),
      filter(clickIdFromUrl => !!clickIdFromUrl),
      tap(clickIdFromUrl => {
        const existingClickId = this.parseClickId(
          this.browserCookieService.getCookieValue(
            MarketingService.FBC_COOKIE_NAME,
          ),
        );
        if (clickIdFromUrl !== existingClickId) {
          const cookieResult = this.buildMetaCookieValue(clickIdFromUrl);
          this.browserCookieService.setCookie(
            MarketingService.FBC_COOKIE_NAME,
            cookieResult.value,
            new Date(
              cookieResult.date +
                MarketingService.FBC_COOKIE_EXPIRATION_DAYS *
                  24 *
                  60 *
                  60 *
                  1000,
            ),
            {
              path: MarketingService.COOKIE_PATH,
            },
          );
        }
      }),
      map(() => undefined),
    );
  }

  private extractQueryParameter(
    searchString: string,
    name: string,
  ): string | null {
    const urlParams = new URLSearchParams(searchString);
    return urlParams.get(name);
  }

  private parseClickId(fbcCookie: string | null): string | null {
    if (!fbcCookie) return null;
    const parts = fbcCookie.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : null;
  }

  private buildMetaCookieValue(fbclid: string): {
    value: string;
    date: number;
  } {
    const version = 'fb';
    const creationTime: number = Date.now();
    const value = `${version}.${MarketingService.SUBDOMAIN_INDEX}.${creationTime}.${fbclid}`;
    return { value, date: creationTime };
  }

  private getSearchFromUrl(url: string): string {
    try {
      if (url) {
        const parsedUrl = new URL(url);
        return parsedUrl.search;
      }
    } catch (e) {}
    return '';
  }
}
